import { Component, ElementRef, HostListener, computed, input, output, signal, viewChild } from '@angular/core'
import { CommonModule } from '@angular/common'
import { actionOutputs } from './workflow-catalog'

export interface WorkflowGraphNode {
  id: string
  order: number
  label: string
  /** Sottotitolo (tipo/tecnologia dell'azione). */
  subtitle?: string
  /** Tecnologia per il colore dell'icona. */
  technology?: 'fortinet' | 'cisco' | 'nrd' | 'generic'
  icon: string
  actionKey: string
  isEntry: boolean
  /** Archi uscenti risolti: [{ on: outputKey, target: stepId }]. */
  targets: { on: string; target: string }[]
  tagsCount: number
  /** Esito di esecuzione (usato dalla vista storico run); null/assente = non eseguito. */
  status?: string | null
  /** Durata e messaggio dell'esecuzione (vista storico). */
  durationMs?: number
  message?: string
  /** Outcome effettivamente preso, per evidenziare l'handle giusto. */
  takenOutcome?: string | null
  /** Step attualmente in esame durante lo scrub della history. */
  current?: boolean
  /** Nodo non (ancora) eseguito: reso attenuato nella vista history. */
  dimmed?: boolean
}

export interface WorkflowGraphConnection {
  from: string
  on: string
  target: string | null
}

interface GraphEdge {
  id: string
  type: 'success' | 'failed' | 'warning' | 'start'
  kind: 'edge' | 'preview' | 'start' | 'end'
  path: string
  label: string
  labelVisible: boolean
  labelX: number
  labelY: number
}

interface GraphPill { x: number; y: number }

interface Point { x: number; y: number }

const NODE_W = 250
const NODE_H = 150
const COL_W = 360
const ROW_H = 250
const ORIGIN_X = 190
const ORIGIN_Y = 40
const MAX_CURVE_DX = 220
const PILL_W = 88
const PILL_H = 36

@Component({
  selector: 'app-workflow-graph',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './workflow-graph.component.html',
  styleUrls: ['./workflow-graph.component.scss']
})
export class WorkflowGraphComponent {
  readonly nodes = input.required<WorkflowGraphNode[]>()
  readonly selectedId = input<string | null>(null)
  /** Blocco dopo cui posizionare il nodo appena aggiunto. */
  readonly anchorId = input<string | null>(null)
  /** false = sola visualizzazione (storico run): niente drag né collegamenti. */
  readonly editable = input(true)

  readonly selectNode = output<string>()
  readonly connectNode = output<WorkflowGraphConnection>()
  readonly nodeAction = output<{ action: 'entry' | 'duplicate' | 'remove'; nodeId: string }>()
  readonly openMenuId = signal<string | null>(null)

  readonly nodeWidth = NODE_W
  readonly baseNodeHeight = NODE_H

  private readonly canvasRef = viewChild<ElementRef<HTMLElement>>('canvas')

  private readonly manualPositions = signal<Record<string, Point>>({})
  private readonly manualEndPosition = signal<GraphPill | null>(null)
  readonly pendingFrom = signal<string | null>(null)
  readonly pendingOn = signal<string | null>(null)
  readonly dragActive = signal(false)
  readonly dragPoint = signal<Point | null>(null)
  readonly zoom = signal(1)
  readonly minZoom = 0.5
  readonly maxZoom = 1.8
  readonly minimapWidth = 160
  readonly minimapHeight = 100

  /** Layout automatico a livelli (BFS dall'entry). */
  private readonly autoLayout = computed<Record<string, Point>>(() => {
    const nodes = this.nodes()
    const byId = new Map(nodes.map(node => [node.id, node]))
    const entry = nodes.find(node => node.isEntry) ?? nodes[0]
    const level = new Map<string, number>()

    if (entry) {
      level.set(entry.id, 0)
      const queue: string[] = [entry.id]
      while (queue.length) {
        const currentId = queue.shift() as string
        const node = byId.get(currentId)
        if (!node) continue
        const currentLevel = level.get(currentId) ?? 0
        for (const link of node.targets ?? []) {
          const target = link && link.target
          if (target && byId.has(target) && !level.has(target)) {
            level.set(target, currentLevel + 1)
            queue.push(target)
          }
        }
      }
    }

    const groups = new Map<number, string[]>()
    nodes.forEach(node => {
      if (!level.has(node.id)) return
      const l = level.get(node.id) ?? 0
      if (!groups.has(l)) groups.set(l, [])
      ;(groups.get(l) as string[]).push(node.id)
    })

    const positions: Record<string, Point> = {}
    groups.forEach((ids, l) => {
      ids.forEach((id, index) => {
        positions[id] = { x: ORIGIN_X + l * COL_W, y: ORIGIN_Y + index * ROW_H }
      })
    })

    const manual = this.manualPositions()
    // Il nodo appena aggiunto resta accanto al blocco selezionato,
    // anche se questo è stato spostato manualmente.
    const anchor = this.anchorId()
    if (anchor) {
      const anchorIndex = nodes.findIndex(node => node.id === anchor)
      const anchoredNode = anchorIndex >= 0 ? nodes[anchorIndex + 1] : null
      if (anchoredNode && !manual[anchoredNode.id]) {
        const anchorPosition = manual[anchor] || positions[anchor]
        if (anchorPosition) {
          positions[anchoredNode.id] = { x: anchorPosition.x + COL_W, y: anchorPosition.y }
        }
      }
    }

    // I nodi non ancora collegati restano vicini al blocco precedente,
    // usando anche la posizione manuale se l'utente l'ha spostato.
    nodes.forEach((node, index) => {
      if (positions[node.id]) return
      const previous = index > 0 ? nodes[index - 1] : null
      const previousPosition = previous ? (manual[previous.id] || positions[previous.id]) : null
      positions[node.id] = previousPosition
        ? { x: previousPosition.x + COL_W, y: previousPosition.y }
        : { x: ORIGIN_X, y: ORIGIN_Y }
    })

    return positions
  })

  readonly layout = computed<Record<string, Point>>(() => ({
    ...this.autoLayout(),
    ...this.manualPositions()
  }))

  readonly canvasSize = computed(() => {
    const positions = Object.values(this.layout())
    const maxX = positions.reduce((max, p) => Math.max(max, p.x), 0)
    const maxY = positions.reduce((max, p) => Math.max(max, p.y), 0)
    const maxHeight = this.nodes().reduce((max, node) => Math.max(max, this.nodeHeightOf(node)), NODE_H)
    const end = this.manualEndPosition()
    return {
      width: Math.max(maxX + NODE_W + 220, (end?.x ?? 0) + PILL_W + 120),
      height: Math.max(maxY + maxHeight + 170, (end?.y ?? 0) + PILL_H + 120)
    }
  })

  readonly startPill = computed<GraphPill | null>(() => {
    if (!this.editable()) return null
    const nodes = this.nodes()
    if (!nodes.length) return null
    const entry = nodes.find(node => node.isEntry) ?? nodes[0]
    const position = this.layout()[entry.id]
    if (!position) return null
    return { x: Math.max(18, position.x - 140), y: position.y + 16 }
  })

  readonly endPill = computed<GraphPill | null>(() => {
    if (!this.editable()) return null
    const manual = this.manualEndPosition()
    if (manual) return manual
    const nodes = this.nodes()
    if (!nodes.length) return null
    const layout = this.layout()
    const lastNode = nodes.reduce((last, node) => (layout[node.id]?.x ?? 0) > (layout[last.id]?.x ?? 0) ? node : last, nodes[0])
    const bottom = nodes.reduce((max, node) => Math.max(max, (layout[node.id]?.y ?? 0) + this.nodeHeightOf(node)), 0)
    const lastPosition = layout[lastNode.id]
    return {
      x: (lastPosition?.x ?? 0) + (NODE_W - PILL_W) / 2,
      y: bottom + 70
    }
  })

  readonly minimapScale = computed(() => {
    const size = this.canvasSize()
    return Math.min(
      this.minimapWidth / Math.max(1, size.width),
      this.minimapHeight / Math.max(1, size.height)
    )
  })

  /** Altezza del nodo: cresce con messaggio di errore e numero di output. */
  nodeHeightOf (node: WorkflowGraphNode): number {
    const outputCount = actionOutputs(node.actionKey).length
    return NODE_H + (node.message ? 40 : 0) + (outputCount > 2 ? 20 : 0)
  }

  /** Spazio inferiore riservato agli output, così il footer resta sopra le label. */
  nodePaddingBottom (node: WorkflowGraphNode): number {
    const count = Math.max(1, actionOutputs(node.actionKey).length)
    return 25 + (count - 1) * 20
  }

  /** Controllo orizzontale della curva: più ampio sui collegamenti verticali. */
  curveDx (sx: number, sy: number, tx: number, ty: number): number {
    return Math.max(80, Math.min(Math.abs(tx - sx) * 0.5 + Math.abs(ty - sy) * 0.35, MAX_CURVE_DX))
  }

  /** Posizione verticale relativa (dentro il nodo) dell'handle di un output. */
  handleTop (node: WorkflowGraphNode, on: string): number {
    const outputs = actionOutputs(node.actionKey)
    const count = Math.max(1, outputs.length)
    const index = Math.max(0, outputs.findIndex(output => output.key === on))
    const height = this.nodeHeightOf(node)
    if (count <= 1) return height - 30
    const bottom = height - 16
    const gap = 20
    return bottom - (count - 1 - index) * gap
  }

  outputSeverity (node: WorkflowGraphNode, on: string): 'success' | 'failed' | 'warning' {
    const output = actionOutputs(node.actionKey).find(item => item.key === on)
    const severity = output?.severity ?? (on === 'failed' ? 'failed' : 'success')
    return severity === 'failed' ? 'failed' : severity === 'warning' ? 'warning' : 'success'
  }

  outputLabel (node: WorkflowGraphNode, on: string): string {
    const output = actionOutputs(node.actionKey).find(item => item.key === on)
    return output?.label ?? on
  }

  readonly edges = computed<GraphEdge[]>(() => {
    const nodes = this.nodes()
    const positions = this.layout()
    const edges: GraphEdge[] = []

    const labelOf = (node: WorkflowGraphNode, on: string): string => this.outputLabel(node, on)

    // Primo passaggio: conteggio degli archi entranti per ogni nodo (per separare le frecce).
    const links: { node: WorkflowGraphNode; on: string; target: string }[] = []
    nodes.forEach(node => {
      (node.targets ?? []).forEach(link => {
        if (link && link.target) links.push({ node, on: link.on, target: link.target })
      })
    })
    const incomingCount = new Map<string, number>()
    const outgoingCount = new Map<string, number>()
    links.forEach(({ node, target }) => {
      incomingCount.set(target, (incomingCount.get(target) ?? 0) + 1)
      outgoingCount.set(node.id, (outgoingCount.get(node.id) ?? 0) + 1)
    })
    const incomingSeen = new Map<string, number>()
    const nodeById = new Map(nodes.map(node => [node.id, node]))

    links.forEach(({ node, on, target }, index) => {
      const source = positions[node.id]
      const destination = positions[target]
      if (!source || !destination) return
      const targetNode = nodeById.get(target)
      const isLinearNext = !!targetNode && targetNode.order === node.order + 1
      const labelVisible = !(outgoingCount.get(node.id) === 1 && isLinearNext)

      const seen = incomingSeen.get(target) ?? 0
      incomingSeen.set(target, seen + 1)
      const count = incomingCount.get(target) ?? 1
      const targetOffset = count > 1 ? (seen - (count - 1) / 2) * 14 : 0

      const sx = source.x + NODE_W
      const sy = source.y + this.handleTop(node, on)
      const tx = destination.x
      const targetHeight = targetNode ? this.nodeHeightOf(targetNode) : NODE_H
      const ty = destination.y + targetHeight / 2 + targetOffset
      const dx = this.curveDx(sx, sy, tx, ty)
      edges.push({
        id: `${node.id}-${on}-${index}`,
        type: this.outputSeverity(node, on),
        kind: 'edge',
        label: labelOf(node, on),
        labelVisible,
        path: `M ${sx} ${sy} C ${sx + dx} ${sy}, ${tx - dx} ${ty}, ${tx} ${ty}`,
        labelX: (sx + tx) / 2,
        labelY: (sy + ty) / 2 - 6
      })
    })

    // Inizio: pillola a sinistra collegata allo step di avvio.
    const start = this.startPill()
    const entryNode = nodes.find(node => node.isEntry) ?? nodes[0]
    if (start && entryNode) {
      const entryPosition = positions[entryNode.id]
      if (entryPosition) {
        const sx = start.x + PILL_W
        const sy = start.y + PILL_H / 2
        const tx = entryPosition.x
        const ty = entryPosition.y + 30
        const dx = this.curveDx(sx, sy, tx, ty)
        edges.push({
          id: 'start-edge',
          type: 'start',
          kind: 'start',
          label: '',
          labelVisible: false,
          path: `M ${sx} ${sy} C ${sx + dx} ${sy}, ${tx - dx} ${ty}, ${tx} ${ty}`,
          labelX: 0,
          labelY: 0
        })
      }
    }

    // Fine: ogni output non collegato porta alla pillola "Fine".
    const end = this.endPill()
    if (end) {
      nodes.forEach(node => {
        const source = positions[node.id]
        if (!source) return
        const connected = new Set((node.targets ?? []).map(link => link.on))
        actionOutputs(node.actionKey).forEach(output => {
          if (connected.has(output.key)) return
          const sx = source.x + NODE_W
          const sy = source.y + this.handleTop(node, output.key)
          const tx = end.x
          const ty = end.y + PILL_H / 2
          const dx = this.curveDx(sx, sy, tx, ty)
          edges.push({
            id: `${node.id}-${output.key}-end`,
            type: this.outputSeverity(node, output.key),
            kind: 'end',
            label: this.outputLabel(node, output.key),
            labelVisible: false,
            path: `M ${sx} ${sy} C ${sx + dx} ${sy}, ${tx - dx} ${ty}, ${tx} ${ty}`,
            labelX: 0,
            labelY: 0
          })
        })
      })
    }

    // Anteprima del collegamento mentre si trascina un output.
    const from = this.pendingFrom()
    const on = this.pendingOn()
    const point = this.dragPoint()
    const sourceNode = from ? nodes.find(node => node.id === from) : undefined
    if (this.dragActive() && from && on && point && positions[from] && sourceNode) {
      const source = positions[from]
      const sx = source.x + NODE_W
      const sy = source.y + this.handleTop(sourceNode, on)
      const tx = point.x
      const ty = point.y
      const dx = this.curveDx(sx, sy, tx, ty)
      edges.push({
        id: 'connect-preview',
        type: this.outputSeverity(sourceNode, on),
        kind: 'preview',
        label: labelOf(sourceNode, on),
        labelVisible: true,
        path: `M ${sx} ${sy} C ${sx + dx} ${sy}, ${tx - dx} ${ty}, ${tx} ${ty}`,
        labelX: (sx + tx) / 2,
        labelY: (sy + ty) / 2 - 6
      })
    }

    return edges
  })

  /** Output dichiarati dall'azione del nodo (per i handle / drag-to-connect). */
  outputsFor (node: WorkflowGraphNode): { key: string; label: string; severity: string }[] {
    return actionOutputs(node.actionKey)
  }

  outputSeverityClass (node: WorkflowGraphNode, on: string): string {
    return this.outputSeverity(node, on)
  }

  pendingLabel (): string {
    const from = this.pendingFrom()
    const on = this.pendingOn()
    if (!from || !on) return ''
    const node = this.nodes().find(item => item.id === from)
    const output = node ? actionOutputs(node.actionKey).find(item => item.key === on) : undefined
    return output?.label ?? on
  }

  positionOf (id: string): Point {
    return this.layout()[id] ?? { x: 0, y: 0 }
  }

  statusLabel (status?: string | null): string {
    const labels: Record<string, string> = {
      success: 'Completato',
      completed: 'Completato',
      failed: 'Fallito',
      warning: 'Warning',
      neutral: 'Neutral',
      skipped: 'Saltato',
      running: 'In corso',
      queued: 'In coda'
    }
    return labels[String(status || '').toLowerCase()] ?? (status || '')
  }

  statusIcon (status?: string | null): string {
    const key = String(status || '').toLowerCase()
    if (key === 'failed') return 'fa-solid fa-circle-xmark'
    if (key === 'warning' || key === 'queued') return 'fa-solid fa-triangle-exclamation'
    if (key === 'running') return 'fa-solid fa-spinner fa-spin'
    if (key === 'neutral' || key === 'skipped') return 'fa-solid fa-circle-minus'
    return 'fa-solid fa-circle-check'
  }

  formatDuration (durationMs?: number): string {
    if (!durationMs || durationMs <= 0) return ''
    if (durationMs < 1000) return `${durationMs} ms`
    const seconds = durationMs / 1000
    return seconds < 60 ? `${seconds.toFixed(0)}s` : `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`
  }

  statusSummary (node: WorkflowGraphNode): string {
    const label = this.statusLabel(node.status)
    const duration = this.formatDuration(node.durationMs)
    return duration ? `${label} · ${duration}` : label
  }

  isPendingFrom (id: string): boolean {
    return this.pendingFrom() === id
  }

  private justDragged = false

  onHandleDragStart (event: DragEvent, node: WorkflowGraphNode, on: string): void {
    if (!this.editable()) return
    event.stopPropagation()
    const payload = JSON.stringify({ from: node.id, on })
    event.dataTransfer?.setData('application/x-awp-connect', payload)
    event.dataTransfer?.setData('text/plain', payload)
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'link'
    this.pendingFrom.set(node.id)
    this.pendingOn.set(on)
    this.dragActive.set(true)
    this.updateDragPoint(event)
  }

  onHandleDragEnd (): void {
    this.justDragged = true
    setTimeout(() => { this.justDragged = false }, 0)
    this.cancelPending()
  }

  allowDrop (event: DragEvent): void {
    if (!this.editable()) return
    event.preventDefault()
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'link'
    if (this.dragActive()) this.updateDragPoint(event)
  }

  private updateDragPoint (event: DragEvent): void {
    const container = this.canvasRef()?.nativeElement
    if (!container) return
    const rect = container.getBoundingClientRect()
    this.dragPoint.set({
      x: (event.clientX - rect.left + container.scrollLeft) / this.zoom(),
      y: (event.clientY - rect.top + container.scrollTop) / this.zoom()
    })
  }

  private readConnection (event: DragEvent): { from: string; on: string } | null {
    try {
      const raw = event.dataTransfer?.getData('application/x-awp-connect') || event.dataTransfer?.getData('text/plain')
      if (!raw) return null
      const parsed = JSON.parse(raw)
      if (parsed && parsed.from && parsed.on) return { from: parsed.from, on: parsed.on }
    } catch {
      return null
    }
    return null
  }

  onNodeDrop (event: DragEvent, node: WorkflowGraphNode): void {
    if (!this.editable()) return
    event.preventDefault()
    event.stopPropagation()
    const connection = this.readConnection(event)
    if (connection && connection.from !== node.id) {
      this.connectNode.emit({ from: connection.from, on: connection.on, target: node.id })
    }
    this.cancelPending()
  }

  onCanvasDrop (event: DragEvent): void {
    if (!this.editable()) return
    event.preventDefault()
    const connection = this.readConnection(event)
    if (connection) {
      // Rilasciato sullo sfondo: "Fine" (termina il ramo).
      this.connectNode.emit({ from: connection.from, on: connection.on, target: null })
    }
    this.cancelPending()
  }

  onHandleClick (event: Event, node: WorkflowGraphNode, on: string): void {
    event.stopPropagation()
    if (!this.editable()) return
    if (this.justDragged) return
    if (this.pendingFrom() === node.id && this.pendingOn() === on) {
      this.cancelPending()
      return
    }
    this.pendingFrom.set(node.id)
    this.pendingOn.set(on)
  }

  toggleNodeMenu (event: Event, node: WorkflowGraphNode): void {
    event.preventDefault()
    event.stopPropagation()
    this.openMenuId.update(current => current === node.id ? null : node.id)
  }

  emitNodeAction (event: Event, node: WorkflowGraphNode, action: 'entry' | 'duplicate' | 'remove'): void {
    event.preventDefault()
    event.stopPropagation()
    this.openMenuId.set(null)
    this.nodeAction.emit({ action, nodeId: node.id })
  }

  onNodeClick (event: Event, node: WorkflowGraphNode): void {
    event.stopPropagation()
    this.openMenuId.set(null)
    if (!this.editable()) {
      this.selectNode.emit(node.id)
      return
    }
    if (this.pendingFrom() && this.pendingOn()) {
      if (node.id === this.pendingFrom()) return
      this.connectNode.emit({ from: this.pendingFrom() as string, on: this.pendingOn() as string, target: node.id })
      this.cancelPending()
      return
    }
    this.selectNode.emit(node.id)
  }

  onCanvasClick (): void {
    this.openMenuId.set(null)
    if (!this.editable()) return
    if (this.pendingFrom() && this.pendingOn()) {
      this.connectNode.emit({ from: this.pendingFrom() as string, on: this.pendingOn() as string, target: null })
      this.cancelPending()
    }
  }

  cancelPending (): void {
    this.pendingFrom.set(null)
    this.pendingOn.set(null)
    this.dragActive.set(false)
    this.dragPoint.set(null)
  }

  @HostListener('document:keydown.escape')
  onEscape (): void {
    this.openMenuId.set(null)
    this.cancelPending()
  }

  // ─────────────────────────────── drag ───────────────────────────────
  private dragId: string | null = null
  private dragOffset: Point = { x: 0, y: 0 }
  private containerOrigin: Point = { x: 0, y: 0 }
  private draggingEnd = false
  private endDragOffset: Point = { x: 0, y: 0 }

  onEndPointerDown (event: PointerEvent): void {
    if (!this.editable()) return
    const container = this.canvasRef()?.nativeElement
    const end = this.endPill()
    if (!container || !end) return
    event.preventDefault()
    event.stopPropagation()
    const rect = container.getBoundingClientRect()
    this.containerOrigin = { x: rect.left, y: rect.top }
    this.endDragOffset = {
      x: (event.clientX - rect.left + container.scrollLeft) / this.zoom() - end.x,
      y: (event.clientY - rect.top + container.scrollTop) / this.zoom() - end.y
    }
    this.draggingEnd = true
  }

  onNodePointerDown (event: PointerEvent, node: WorkflowGraphNode): void {
    if (!this.editable()) return
    const target = event.target as HTMLElement
    // Gli handle e il menu hanno il proprio comportamento: non devono avviare
    // anche il trascinamento del nodo.
    if (target.closest('.wg-handles, .wg-handle-row, .wg-handle-dot, .wg-handle-label, .wg-node-menu, .wg-node-menu-panel')) return
    const container = this.canvasRef()?.nativeElement
    if (!container) return
    const rect = container.getBoundingClientRect()
    this.containerOrigin = { x: rect.left, y: rect.top }
    const position = this.positionOf(node.id)
    this.dragOffset = {
      x: (event.clientX - this.containerOrigin.x + container.scrollLeft) / this.zoom() - position.x,
      y: (event.clientY - this.containerOrigin.y + container.scrollTop) / this.zoom() - position.y
    }
    this.dragId = node.id
  }

  @HostListener('document:pointermove', ['$event'])
  onPointerMove (event: PointerEvent): void {
    const container = this.canvasRef()?.nativeElement
    const scrollLeft = container ? container.scrollLeft : 0
    const scrollTop = container ? container.scrollTop : 0

    if (this.draggingEnd) {
      const x = Math.max(0, (event.clientX - this.containerOrigin.x + scrollLeft) / this.zoom() - this.endDragOffset.x)
      const y = Math.max(0, (event.clientY - this.containerOrigin.y + scrollTop) / this.zoom() - this.endDragOffset.y)
      this.manualEndPosition.set({ x, y })
      return
    }

    if (!this.dragId) return
    const x = Math.max(0, (event.clientX - this.containerOrigin.x + scrollLeft) / this.zoom() - this.dragOffset.x)
    const y = Math.max(0, (event.clientY - this.containerOrigin.y + scrollTop) / this.zoom() - this.dragOffset.y)
    this.manualPositions.update(positions => ({ ...positions, [this.dragId as string]: { x, y } }))
  }

  @HostListener('document:pointerup')
  onPointerUp (): void {
    this.dragId = null
    this.draggingEnd = false
  }

  resetLayout (): void {
    this.manualPositions.set({})
    this.manualEndPosition.set(null)
    this.cancelPending()
  }

  zoomIn (): void {
    this.zoom.update(value => Math.min(this.maxZoom, Number((value + 0.1).toFixed(2))))
  }

  zoomOut (): void {
    this.zoom.update(value => Math.max(this.minZoom, Number((value - 0.1).toFixed(2))))
  }

  resetZoom (): void {
    this.zoom.set(1)
  }

  fitToView (): void {
    const container = this.canvasRef()?.nativeElement
    if (!container) return
    const size = this.canvasSize()
    const scale = Math.min(
      (container.clientWidth - 24) / Math.max(1, size.width),
      (container.clientHeight - 24) / Math.max(1, size.height)
    )
    this.zoom.set(Math.max(this.minZoom, Math.min(this.maxZoom, Number(scale.toFixed(2)))))
    container.scrollTo({ left: 0, top: 0, behavior: 'smooth' })
  }

  onCanvasWheel (event: WheelEvent): void {
    if (!event.ctrlKey && !event.metaKey) return
    event.preventDefault()
    if (event.deltaY < 0) this.zoomIn()
    else this.zoomOut()
  }

  scrollToMinimap (event: MouseEvent): void {
    const container = this.canvasRef()?.nativeElement
    const target = event.currentTarget as HTMLElement
    if (!container || !target) return
    const rect = target.getBoundingClientRect()
    const ratioX = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width))
    const ratioY = Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height))
    const size = this.canvasSize()
    container.scrollTo({
      left: Math.max(0, ratioX * size.width * this.zoom() - container.clientWidth / 2),
      top: Math.max(0, ratioY * size.height * this.zoom() - container.clientHeight / 2),
      behavior: 'smooth'
    })
  }
}
